/**
 * Объекты. Модуль для работы с API
 * @module objects
 */

import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

const normalizeObjectPayload = (item = {}) =>
  Object.entries(item).reduce((acc, [key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      acc[key] = value.uuid ?? value.file_uuid ?? value.file?.uuid ?? value;
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

/**
 * Получение списка объектов. Поддерживает пагинацию, поиск и фильтры
 * @param  {Object} params - Параметры запроса
 * @param {string} params.definition_uuid - uuid описателя
 * @param {string} params.page - номер страницы
 * @param {string} params.perpage - количество объектов на странице
 * @param {string} params.search - строка поиска
 * @param {string} params.sort - сортировка
 * @returns {Promise} - список объектов
 * @throws {Error} - ошибка при получении данных
 */
export const getObjects = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/objects${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка ресурсов без пагинации
 * @param  {Object} params - Параметры запроса
 * @returns {Promise} - список объектов
 * @throws {Error} - ошибка при получении данных
 */
export const getObjectsOptions = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(
      `/api/objects${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка ресурсов с типом объект без пагинации
 * @param  {Object} params - Параметры запроса
 * @returns {Promise} - список объектов
 * @throws {Error} - ошибка при получении данных
 */
export const getObjectsReferenceOptions = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(
      `/api/objects/reference-options${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение объекта
 * @param  {String} definition_uuid - uuid описателя
 * @param  {String} uuid - uuid объекта
 * @returns {Promise} - объект
 * @throws {Error} - ошибка при получении данных
 */
export const getObject = async (definition_uuid, uuid, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/object${
        isAdmin ? "/admin" : ""
      }?definition_uuid=${definition_uuid}&uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${
          data.error ? data.error : "Обновите страницу"
        }`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание объекта
 * @param  {Object} item - данные объекта
 * @returns {Promise} - созданный объект
 * @throws {Error} - ошибка при создании данных
 */
export const createObject = async (item) => {
  try {
    const response = await fetch("/api/object", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(normalizeObjectPayload(item)),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.object;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление объекта
 * @param  {Object} item - данные объекта
 * @returns {Promise} - обновленный объект
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateObject = async (item) => {
  try {
    const response = await fetch("/api/object", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(normalizeObjectPayload(item)),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.object;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление объекта
 * @param  {Object} item - данные объекта
 * @param  {string} item.uuid - обязательное поле: UUID объекта
 * @param  {string} item.definition_uuid - обязательное поле: UUID описателя
 * @returns {Promise} - удаленный объект
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteObject = async (item) => {
  try {
    const response = await fetch("/api/object", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.object;
  } catch (err) {
    throw err;
  }
};

/**
 * Экспорт объектов
 * @param  {Object} item - параметры запроса
 * @param {string} item.definition_uuid - UUID описателя
 * @param {Array} item.objects - массив UUID объектов
 * @param {Array} item.fields - массив UUID полей
 * @param {string} item.file_type - тип файла
 * @returns {Promise} - файл
 * @throws {Error} - ошибка при выгрузке данных
 */
export const exportObjects = async (item, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/objects/export${isAdmin ? "/admin" : ""}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(item),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        `Ошибка при выгрузке данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.file;
  } catch (err) {
    throw err;
  }
};

/**
 * Экспорт объектa
 * @param  {Object} item - параметры запроса
 * @param {string} item.definition_uuid - UUID описателя
 * @param {Array} item.object - UUID объекта
 * @param {Array} item.fields - массив UUID полей
 * @returns {Promise} - файл
 * @throws {Error} - ошибка при выгрузке данных
 */
export const exportObject = async (item, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/object/export-xlsx${isAdmin ? "/admin" : ""}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(item),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        `Ошибка при выгрузке данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.file;
  } catch (err) {
    throw err;
  }
};

/**
 * Скачивание шаблона данных для последующего импорта
 * @param  {Object} item - параметры запроса
 * @param {string} item.definition_uuid - UUID описателя
 * @param {string} item.objects - объекты для скачивания
 * @returns {Promise} - файл
 * @throws {Error} - ошибка при выгрузке данных
 */
export const downloadTemplate = async (item, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/objects/export-xlsx/import-format${isAdmin ? "/admin" : ""}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(item),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        `Ошибка при выгрузке данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.file;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение паспорта объекта
 * @param  {String} definition_uuid - uuid описателя
 * @param  {String} uuid - uuid объекта
 * @returns {Promise} - данные для открытия страницы паспорта объекта
 * @throws {Error} - ошибка при получении данных
 */
export const getPassport = async (definition_uuid, uuid) => {
  try {
    const response = await fetch(`/api/object-passport`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ definition_uuid, object_uuid: uuid }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${
          data.error ? data.error : "Обновите страницу"
        }`,
      );
    }

    return data.passportRecord;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение данных для страницы Паспорт объекта
 * @param  {String} uuid - uuid паспорта
 * @returns {Promise} - данные для страницы паспорта объекта
 * @throws {Error} - ошибка при получении данных
 */
export const getPassportData = async (uuid) => {
  try {
    const response = await fetch(`/api/public/object-passport?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${
          data.error ? data.error : "Обновите страницу"
        }`,
      );
    }

    return data.passport;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить связанные задачи для объекта
 * @param {Object} params - {uuid } uuid - id объекта
 * @returns {Promise<Object>} - Содержит связанные задачи объекта
 * @throws {Error} - Ошибка при получении данных.
 */
export const getObjectTasks = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/object/tasks${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.tasks || [];
  } catch (err) {
    throw err;
  }
};

/**
 * Получить связанные файлы для объекта
 * @param {Object} params - {uuid } uuid - id объекта
 * @returns {Promise<Object>} - Содержит связанные файлы объекта
 * @throws {Error} - Ошибка при получении данных.
 */
export const getObjectFiles = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/object/files${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

export const attachObjectFile = async (payload) => {
  const response = await fetch(
    `/api/object/file`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(data.error || "Не удалось прикрепить файл");
  }

  return data;
};

export const detachObjectFile = async (payload) => {
  const response = await fetch(
    `/api/object/file`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(data.error || "Не удалось открепить файл");
  }

  return data;
};

/**
 * Импорт объектов из файла в формате xlsx
 * @param  {FormData} formData - данные формы
 * @returns {Promise} - данные об импорте
 * @throws {Error} - ошибка при импорте данных
 */
export const importObjects = async (formData, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/objects/import-xlsx/from-import-format${isAdmin ? "/admin" : ""}`,
      {
        method: "POST",

        body: formData,
      },
    );

    const data = await response.json();

    // if (!response.ok || data.error) {
    //   throw new Error(
    //     `Ошибка при импорте данных. ${data.error || "Попробуйте снова"}`
    //   );
    // }

    return data;
  } catch (err) {
    throw err;
  }
};

export const getMapLayer = async (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (key !== "definitions_uuids") {
      searchParams.append(key, value);
    }
  });

  if (params?.definitions_uuids?.length) {
    params.definitions_uuids.forEach((definition) => {
      searchParams.append("definitions_uuids[]", definition);
    });
  }

  const queryString = searchParams.toString();
  try {
    const response = await fetch(`/api/map/layer?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

export const getMapEditContext = async (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    searchParams.append(key, value);
  });

  const queryString = searchParams.toString();
  try {
    const response = await fetch(`/api/map/edit-context?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

export const getMapDefinitions = async () => {
  try {
    const response = await fetch(`/api/map/filters/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};
